import { apiClient } from '../client'
import type {
  Database,
  QueryRequest,
  QueryResult,
  RestoreProgress,
  SnapshotCommit,
  TableDetail,
  TableInfo,
} from '@milliswift/types'

/**
 * Console data: databases, tables, queries and snapshots.
 *
 * ⚠️  THE PLATFORM DOES NOT SERVE THESE YET.
 *
 * There is no route and no table for any of them (see
 * platform/router/router.go and platform/db/migrations). These calls are the
 * intended contract, written now so the console UI is complete and so the
 * backend has a precise target. `docs/PLATFORM_API_GAPS.md` is the handoff.
 *
 * Until the endpoints exist, requests are answered by the fixtures in
 * ../mocks whenever VITE_USE_MOCKS is on. Integration later is a config
 * change, not a rewrite: set VITE_USE_MOCKS=false and these calls go to the
 * real API unchanged.
 *
 * Every page rendering this data must show <PreviewDataBadge />.
 */
export const consoleApi = {
  // contract: PLATFORM_API_GAPS.md#get-apiv1databases
  listDatabases: () => apiClient.get<Database[]>('/api/v1/databases').then((r) => r.data),

  // contract: PLATFORM_API_GAPS.md#get-apiv1tables
  listTables: (databaseId: string) =>
    apiClient
      .get<TableInfo[]>('/api/v1/tables', { params: { database_id: databaseId } })
      .then((r) => r.data),

  // contract: PLATFORM_API_GAPS.md#get-apiv1tablesname
  getTable: (databaseId: string, tableName: string) =>
    apiClient
      .get<TableDetail>(`/api/v1/tables/${encodeURIComponent(tableName)}`, {
        params: { database_id: databaseId },
      })
      .then((r) => r.data),

  // contract: PLATFORM_API_GAPS.md#post-apiv1query
  runQuery: (data: QueryRequest) =>
    apiClient.post<QueryResult>('/api/v1/query', data).then((r) => r.data),

  // contract: PLATFORM_API_GAPS.md#get-apiv1snapshots
  listSnapshots: (databaseId: string) =>
    apiClient
      .get<SnapshotCommit[]>('/api/v1/snapshots', { params: { database_id: databaseId } })
      .then((r) => r.data),

  // contract: PLATFORM_API_GAPS.md#post-apiv1snapshotscommit_hashrestore
  restoreSnapshot: (databaseId: string, commitHash: string) =>
    apiClient
      .post(`/api/v1/snapshots/${commitHash}/restore`, { database_id: databaseId })
      .then((r) => r.data),

  /**
   * Polled while a restore runs. Returns the same ordered step list the UI
   * renders as a progress tracker.
   */
  restoreProgress: (databaseId: string, commitHash: string) =>
    apiClient
      .get<RestoreProgress>(`/api/v1/snapshots/${commitHash}/restore`, {
        params: { database_id: databaseId },
      })
      .then((r) => r.data),
}
